import { buildLightningBinaryUrl } from "@/api/iceberg/IcebergUrl";
import {
  type AsyncResult,
  SynchrounousState,
  createNotRequestedAsyncResult,
  createPendingAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import {
  type CacheDatum,
  CacheDatumState,
  type LoadedCacheDatum,
  type PermanentlyFailedCacheDatum,
} from "@/cache/CacheDatumState";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { cancelable } from "@/cache/utils";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "@/cache/utils/cacheEviction";
import { sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import { canonicalizeLightningWindow } from "@/layers/geojson/LayerUtils";
import type { LightningBinaryPayload } from "@/layers/lightning-binary/LightningBinaryPayload";
import type { LightningBinaryRequest } from "@/layers/lightning-binary/LightningBinaryRequest";
import { recordLayerFetchMs } from "@/otel/metrics";
import { MiB } from "@/utility/bytesize";
import { DateTime, Duration } from "luxon";

const DEFAULT_MAX_SIZE = MiB(128);
const DEFAULT_DELAYED_EVICTION_SIZE = MiB(160);
const LIVE_TTL_MS = 60_000;
const FAILURE_RETRY_TTL_MS = 60_000;
const LIVE_PAST_OFFSET = Duration.fromObject({ minutes: 10 });
const LIVE_FUTURE_OFFSET = Duration.fromObject({ hours: 3 });

type PendingMeta = {
  owners: Set<string>;
  hasAnonymousConsumer: boolean;
};

export type LightningBinaryCacheStats = {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
};

export type LightningBinaryCacheOptions = {
  now?: () => DateTime;
  monotonicNow?: () => number;
};

function canonicalTypeFilter(request: LightningBinaryRequest): LightningBinaryRequest["typeFilter"] {
  if (!request.typeFilter?.length) {
    return undefined;
  }
  return [...new Set(request.typeFilter)].sort((left, right) => left - right);
}

function isAbortFailure(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (("kind" in error && error.kind === "Abort") || ("isCancelled" in error && error.isCancelled === true))
  );
}

export function canonicalizeLightningBinaryRequest(request: LightningBinaryRequest): LightningBinaryRequest {
  return canonicalizeLightningWindow({ ...request, typeFilter: canonicalTypeFilter(request) });
}

export function lightningBinaryCacheId(request: LightningBinaryRequest): string {
  return buildLightningBinaryUrl(canonicalizeLightningBinaryRequest(request)).toString();
}

export class LightningBinaryCache implements Stats<LightningBinaryCacheStats> {
  private cache: Record<string, CacheDatum<LightningBinaryPayload, null>> = {};
  private requestMeta = new Map<string, LightningBinaryRequest>();
  private pendingMeta = new Map<string, PendingMeta>();
  private requestTokens = new Map<string, symbol>();
  private readonly now: () => DateTime;
  private readonly monotonicNow: () => number;

  constructor(
    private readonly label_: string,
    private readonly maxSize: number = DEFAULT_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_DELAYED_EVICTION_SIZE,
    options: LightningBinaryCacheOptions = {},
  ) {
    this.now = options.now ?? (() => DateTime.utc());
    this.monotonicNow = options.monotonicNow ?? (() => performance.now());
  }

  label(): string {
    return this.label_;
  }

  descJsx(): JSX.Element {
    return jsxTable({ description: "Immutable, zero-copy Iceberg lightning response cache." }, defaultCacheFormatter);
  }

  stats(): LightningBinaryCacheStats {
    const values = Object.values(this.cache);
    return {
      count: values.length,
      pending: values.filter((entry) => entry.state === CacheDatumState.Pending).length,
      estimatedMemoryConsumption: sumLoadedCacheMemoryConsumption(this.cache),
    };
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  clear(): void {
    const retained: typeof this.cache = {};
    const retainedRequests = new Map<string, LightningBinaryRequest>();
    for (const [key, entry] of Object.entries(this.cache)) {
      const request = this.requestMeta.get(key);
      if (entry.state === CacheDatumState.Loaded && request && !this.isLive(request)) {
        retained[key] = entry;
        retainedRequests.set(key, request);
      } else if (entry.state === CacheDatumState.Pending) {
        entry.cancel?.();
      }
    }
    this.cache = retained;
    this.requestMeta = retainedRequests;
    this.pendingMeta.clear();
  }

  peek(request: LightningBinaryRequest): AsyncResult<LightningBinaryPayload> {
    return this.peekById(lightningBinaryCacheId(request));
  }

  peekById(cacheId: string): AsyncResult<LightningBinaryPayload> {
    const entry = this.cache[cacheId];
    if (!entry) {
      return createNotRequestedAsyncResult(undefined as unknown as LightningBinaryPayload);
    }
    if (entry.state === CacheDatumState.Pending) {
      return createPendingAsyncResult(entry.promise, entry.cancel);
    }
    if (entry.state === CacheDatumState.Loaded) {
      markCacheDatumUsed(entry);
      return createResolvedAsyncResult(entry.payload);
    }
    return createNotRequestedAsyncResult(undefined as unknown as LightningBinaryPayload);
  }

  retrieve(request: LightningBinaryRequest, options: { owner?: string } = {}): AsyncResult<LightningBinaryPayload> {
    const canonical = canonicalizeLightningBinaryRequest(request);
    const cacheId = buildLightningBinaryUrl(canonical).toString();
    const existing = this.cache[cacheId];
    if (existing) {
      if (existing.state === CacheDatumState.Pending) {
        this.registerConsumer(cacheId, options.owner);
        return createPendingAsyncResult(existing.promise, existing.cancel);
      }
      if (existing.state === CacheDatumState.Loaded && (!this.isLive(canonical) || !this.isExpired(existing))) {
        markCacheDatumUsed(existing);
        return createResolvedAsyncResult(existing.payload);
      }
      if (existing.state === CacheDatumState.PermanentlyFailed && !this.isFailureRetryDue(existing)) {
        // Animation calls retrieve on every render. Treat a recent failure as unavailable
        // so the frame can advance instead of immediately returning to Pending.
        return createNotRequestedAsyncResult(undefined as unknown as LightningBinaryPayload);
      }
      this.unload(cacheId);
    }

    const requestStarted = this.monotonicNow();
    const requestUrl = buildLightningBinaryUrl(canonical).toString();
    const requestToken = Symbol(cacheId);
    this.requestTokens.set(cacheId, requestToken);
    const cancelablePromise = cancelable(
      apiThreadPool
        .getLightningBinary(canonical)
        .then((payload) => {
          if (this.requestTokens.get(cacheId) !== requestToken) {
            return payload;
          }
          const current = this.cache[cacheId];
          if (!current || current.state !== CacheDatumState.Pending) {
            return payload;
          }
          const loaded: LoadedCacheDatum<LightningBinaryPayload> = {
            state: CacheDatumState.Loaded,
            payload,
            requestDatetime: current.requestDatetime,
            lastUsed: this.monotonicNow(),
            estimatedByteSize: payload.buffer.byteLength,
          };
          this.cache[cacheId] = loaded;
          this.requestTokens.delete(cacheId);
          this.pendingMeta.delete(cacheId);
          this.prune([cacheId]);
          recordLayerFetchMs({
            durationMs: this.monotonicNow() - requestStarted,
            source: "lightning_binary",
            mode: payload.header.aggregation,
            attributes: {
              responseBytes: payload.buffer.byteLength,
              pointCount: payload.header.totalPointCount,
              gridWidth: canonical.width,
              gridHeight: canonical.height,
              wireVersion: payload.header.wireVersion,
            },
          });
          return payload;
        })
        .catch((error) => {
          if (this.requestTokens.get(cacheId) === requestToken) {
            const current = this.cache[cacheId];
            if (isAbortFailure(error) || !current || current.state !== CacheDatumState.Pending) {
              this.unload(cacheId);
            } else {
              // Keep a short-lived failure tombstone. Without it, animation immediately
              // recreates the same unavailable future request and remains rebuffering.
              this.cache[cacheId] = {
                state: CacheDatumState.PermanentlyFailed,
                requestDatetime: this.monotonicNow(),
                payload: null,
              };
              this.requestTokens.delete(cacheId);
              this.pendingMeta.delete(cacheId);
            }
          }
          throw error;
        }),
    );

    let canceled = false;
    const cancel = () => {
      if (canceled) {
        return;
      }
      canceled = true;
      cancelablePromise.cancel();
      apiThreadPool.abortRequestByUrl([requestUrl]);
      if (this.requestTokens.get(cacheId) === requestToken) {
        this.unload(cacheId);
      }
    };

    this.cache[cacheId] = {
      state: CacheDatumState.Pending,
      payload: null,
      requestDatetime: requestStarted,
      promise: cancelablePromise.promise,
      cancel,
    };
    this.requestMeta.set(cacheId, canonical);
    this.registerConsumer(cacheId, options.owner);
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  cancelSuperseded(owner: string, keepPromise?: Promise<LightningBinaryPayload>): number {
    let canceled = 0;
    for (const [cacheId, meta] of this.pendingMeta) {
      if (!meta.owners.has(owner)) {
        continue;
      }
      const entry = this.cache[cacheId];
      if (!entry || entry.state !== CacheDatumState.Pending) {
        this.pendingMeta.delete(cacheId);
        continue;
      }
      if (keepPromise && entry.promise === keepPromise) {
        continue;
      }
      meta.owners.delete(owner);
      if (meta.owners.size === 0 && !meta.hasAnonymousConsumer) {
        entry.cancel?.();
        canceled++;
      }
    }
    return canceled;
  }

  releaseOwner(owner: string): void {
    this.cancelSuperseded(owner);
  }

  private registerConsumer(cacheId: string, owner?: string): void {
    const meta = this.pendingMeta.get(cacheId) ?? { owners: new Set<string>(), hasAnonymousConsumer: false };
    if (owner) {
      meta.owners.add(owner);
    } else {
      meta.hasAnonymousConsumer = true;
    }
    this.pendingMeta.set(cacheId, meta);
  }

  private isLive(request: LightningBinaryRequest): boolean {
    const now = this.now();
    return request.datetime >= now.minus(LIVE_PAST_OFFSET) && request.datetime <= now.plus(LIVE_FUTURE_OFFSET);
  }

  private isExpired(entry: LoadedCacheDatum<LightningBinaryPayload>): boolean {
    return this.monotonicNow() - entry.requestDatetime >= LIVE_TTL_MS;
  }

  private isFailureRetryDue(entry: PermanentlyFailedCacheDatum<null>): boolean {
    return this.monotonicNow() - entry.requestDatetime >= FAILURE_RETRY_TTL_MS;
  }

  private unload(cacheId: string): void {
    delete this.cache[cacheId];
    this.requestMeta.delete(cacheId);
    this.pendingMeta.delete(cacheId);
    this.requestTokens.delete(cacheId);
  }

  private prune(protectedIds: string[]): void {
    const protectedEntries = new Map<string, CacheDatum<LightningBinaryPayload, null>>();
    for (const cacheId of protectedIds) {
      const entry = this.cache[cacheId];
      if (entry) {
        protectedEntries.set(cacheId, entry);
        delete this.cache[cacheId];
      }
    }
    evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, (_entry, key) => this.unload(key));
    for (const [cacheId, entry] of protectedEntries) {
      this.cache[cacheId] = entry;
    }
  }
}

export function isPendingLightningBinaryResult(result: AsyncResult<LightningBinaryPayload>): boolean {
  return result.synchronous === SynchrounousState.StillPending;
}
